'use client'

import { ReactNode, useCallback, useMemo, useState } from 'react'

import { useSession } from '@marketplace-web/shared/session-data'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import {
  getNearbyDropOffPoints,
  transformNearbyDropOffPointDto,
  DropOffPointCarrierModel,
  DropOffPointModel,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { geocodeAddress } from '@marketplace-web/shipping/google-places-feature'
import { getFormattedAddress } from '@marketplace-web/address/address-data'

import DropOffPointMapContext, { DropOffPointMapContextType } from './DropOffPointMapContext'
import { DropOffPointSource, FetchNearbyDropOffPointsArgs } from './types'

type Props = {
  shipmentId?: number
  children: ReactNode
}

const DropOffPointMapContextProvider = ({ shipmentId, children }: Props) => {
  const [uiState, setUiState] = useState(UiState.Idle)
  const [nearbyDropOffPoints, setNearbyDropOffPoints] = useState<
    Array<DropOffPointModel> | undefined
  >()
  const [dropOffPointCarrier, setDropOffPointCarrier] = useState<
    DropOffPointCarrierModel | null | undefined
  >()
  const [selectedDropOffPoint, setSelectedDropOffPoint] = useState<
    DropOffPointModel | null | undefined
  >()
  const [dropOffPointSource, setDropOffPointSource] = useState<string | null | undefined>()

  const { user } = useSession()

  const fetchNearbyDropOffPoints = useCallback(
    async ({
      address,
      coordinates,
      dropOffPointSource: pointSource,
      limit,
      selectedLabelType,
    }: FetchNearbyDropOffPointsArgs) => {
      const userCountryCode = user?.country_iso_code
      const formattedAddress = getFormattedAddress(address)
      const countryCode = address?.countryIsoCode || userCountryCode

      let latitude: number | undefined
      let longitude: number | undefined
      let source: string | undefined

      if (coordinates) {
        latitude = coordinates.latitude
        longitude = coordinates.longitude
      } else if (formattedAddress) {
        const geocodedAddress = await geocodeAddress(formattedAddress)

        latitude = geocodedAddress?.geometry.location.lat()
        longitude = geocodedAddress?.geometry.location.lng()
        source = DropOffPointSource.UserAddress
      }

      if (!shipmentId) return

      setUiState(UiState.Pending)
      setSelectedDropOffPoint(null)

      const response = await getNearbyDropOffPoints({
        shipmentId,
        labelTypeId: selectedLabelType,
        countryCode,
        latitude,
        longitude,
        limit,
        labelType: selectedLabelType,
      })

      if (
        'errors' in response ||
        !response.nearby_drop_off_points?.drop_off_points.length ||
        !response.nearby_drop_off_points?.suggested_drop_off_point_code
      ) {
        setNearbyDropOffPoints([])
        setUiState(UiState.Failure)

        return
      }

      const dropOffPointResponse = transformNearbyDropOffPointDto(response.nearby_drop_off_points)
      const selectedPoint = dropOffPointResponse.dropOffPoints.find(
        point => point.code === dropOffPointResponse.suggestedDropOffPointCode,
      )

      setSelectedDropOffPoint(selectedPoint)
      setNearbyDropOffPoints(dropOffPointResponse.dropOffPoints)
      setDropOffPointCarrier(dropOffPointResponse.carrier)
      setDropOffPointSource(source || pointSource)
      setUiState(UiState.Success)
    },
    [shipmentId, user?.country_iso_code],
  )

  const clearNearbyDropOffPoints = useCallback(() => {
    setNearbyDropOffPoints(undefined)
    setDropOffPointCarrier(undefined)
    setDropOffPointSource(undefined)
    setSelectedDropOffPoint(undefined)
  }, [])

  const selectDropOffPoint = useCallback((point: DropOffPointModel | null | undefined) => {
    setSelectedDropOffPoint(point)
  }, [])

  const contextValues = useMemo<DropOffPointMapContextType>(
    () => ({
      fetchNearbyDropOffPoints,
      clearNearbyDropOffPoints,
      selectDropOffPoint,
      nearbyDropOffPoints,
      selectedDropOffPoint,
      dropOffPointCarrier,
      dropOffPointSource,
      uiState,
    }),
    [
      clearNearbyDropOffPoints,
      dropOffPointCarrier,
      dropOffPointSource,
      fetchNearbyDropOffPoints,
      nearbyDropOffPoints,
      selectedDropOffPoint,
      uiState,
      selectDropOffPoint,
    ],
  )

  return (
    <DropOffPointMapContext.Provider value={contextValues}>
      {children}
    </DropOffPointMapContext.Provider>
  )
}

export default DropOffPointMapContextProvider
